/**
 * Где стоит плавающий «+» SMS-центра. Как в CRM (FeedbackWidget): кнопку можно перетащить —
 * она прилипает к ближайшему краю (левому или правому), высота — где отпустили; место
 * запоминается в браузере. null — место по умолчанию (правый нижний угол).
 *
 * bottom — отступ от низа экрана (без «домашней полоски» iPhone: её CSS прибавляет сам),
 * в пикселях до нижнего края кнопки.
 */
export interface FabPos {
  side: "left" | "right";
  bottom: number;
}

export const FAB_POS_KEY = "fishka.sms.feedback-fab.v1";
export const FAB_SIZE = 52;
/** Сдвиг пальца/мыши меньше этого — это клик, а не перетаскивание. */
export const FAB_DRAG_THRESHOLD = 6;
/** Ближе к низу не ставим — кнопка не должна прилипать к самому краю. */
export const FAB_MIN_BOTTOM = 8;

/** Разбор сохранённого места; мусор и чужие форматы — место по умолчанию. */
export function parseFabPos(raw: string | null): FabPos | null {
  if (!raw) return null;
  try {
    const v = JSON.parse(raw) as Partial<FabPos> | null;
    if (
      v &&
      (v.side === "left" || v.side === "right") &&
      typeof v.bottom === "number" &&
      Number.isFinite(v.bottom)
    ) {
      return { side: v.side, bottom: Math.max(FAB_MIN_BOTTOM, Math.round(v.bottom)) };
    }
  } catch {
    /* не JSON — место по умолчанию */
  }
  return null;
}

export function loadFabPos(): FabPos | null {
  try {
    return parseFabPos(localStorage.getItem(FAB_POS_KEY));
  } catch {
    // приватный режим / хранилище запрещено — место по умолчанию
    return null;
  }
}

/** Запомнить место; null — забыть (вернуть кнопку в правый нижний угол). */
export function saveFabPos(pos: FabPos | null): void {
  try {
    if (pos) localStorage.setItem(FAB_POS_KEY, JSON.stringify(pos));
    else localStorage.removeItem(FAB_POS_KEY);
  } catch {
    // не запомнили — в следующий раз кнопка будет на месте по умолчанию
  }
}

/**
 * Куда «прилипнет» кнопка, когда её отпустили в точке (x, y). safeBottom — «домашняя полоска»
 * (CSS её прибавляет к отступу сам, поэтому вычитаем); topReserve — сколько сверху занято
 * шапкой: выше неё кнопку не ставим.
 */
export function fabPosFromDrop(a: {
  x: number;
  y: number;
  viewportW: number;
  viewportH: number;
  size: number;
  safeBottom: number;
  topReserve: number;
}): FabPos {
  const side: FabPos["side"] = a.x < a.viewportW / 2 ? "left" : "right";
  const wanted = a.viewportH - a.y - a.size / 2 - a.safeBottom;
  const max = a.viewportH - a.safeBottom - a.size - a.topReserve;
  return {
    side,
    bottom: Math.round(Math.max(FAB_MIN_BOTTOM, Math.min(max, wanted))),
  };
}
